import { useLayoutEffect, useRef, useState } from 'react';
import useMediaQuery from './useMediaQuery';

/** Viewport width (the Tailwind ``lg`` breakpoint) from which a side panel docks beside the page. */
const DOCK_MIN_VIEWPORT = '(min-width: 1300px)';

/** Space kept between the page content and a docked side panel, in px. */
const DOCK_GUTTER = 40;

/**
 * Lets a ``SidePanel`` dock beside the page instead of overlaying it. Attach
 * ``shellRef`` and ``shellStyle`` to the element wrapping the page content: while
 * docked it gets right padding equal to however much of it the panel covers (plus
 * a gutter), so content shifts left rather than hiding under the panel. The
 * wrapper's box itself doesn't move, so measuring it never feeds back into itself.
 *
 * Content inside the wrapper should size itself with container queries (mark the
 * wrapper ``@container``) rather than viewport breakpoints, since docking narrows
 * the content without changing the viewport.
 */
const useDockedSidePanel = (open: boolean, panelWidth: number) => {
  const canDock = useMediaQuery(DOCK_MIN_VIEWPORT);
  const docked = open && canDock;
  const shellRef = useRef<HTMLDivElement>(null);
  const [inset, setInset] = useState(0);

  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!docked || !el) {
      setInset(0);
      return;
    }
    const measure = () => {
      const gapToViewportEdge = document.documentElement.clientWidth - el.getBoundingClientRect().right;
      setInset(Math.max(0, panelWidth + DOCK_GUTTER - gapToViewportEdge));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [docked, panelWidth]);

  return { docked, shellRef, shellStyle: { paddingRight: inset } };
};

export default useDockedSidePanel;
